<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';

import Button from 'lib/components/button/Button.vue';
import Dialog from 'lib/components/dialog/Dialog.vue';
import {
  DialogSelectTreeEmits,
  DialogSelectTreeProps,
  DialogSelectTreeSlots,
} from 'lib/components/dialogselecttree/DialogSelectTree.vue.d';
import Tree from 'lib/components/tree/Tree.vue';
import TreeComponent from 'lib/components/tree/Tree.vue.d';
import TreeSearchInput from 'lib/components/treesearchinput/TreeSearchInput.vue';
import { getNestedPropertyValue } from 'lib/utils/object.util';

import { TreeNode, TreeSelectionKeys } from '../basetree/BaseTree.vue.d';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<DialogSelectTreeProps>(), {
  subHeader: undefined,
  lists: undefined,
  listLabel: undefined,
  type: 'group',
  selectionMode: 'checkbox',
  keys: undefined,
  disableNodeWhen: undefined,
  allowEmptySelection: true,
  disableNodeAll: false,
  propagateSelection: true,
});

const emit = defineEmits<DialogSelectTreeEmits>();

const visible = defineModel<boolean>('visible', { default: false });

defineSlots<DialogSelectTreeSlots>();

const DialogPreset = inject<Record<string, any>>('preset', {}).dialog;

const treeComponent = ref<TreeComponent>();
const treeFilter = ref<string>('');
const invalidSelectionMessage = ref<string>('');

const selectedTreeNodes = ref<TreeNode[]>([]);
const selectedTreeKeys = ref<TreeSelectionKeys>();
/**
 * Store the selected node for single selection.
 */
const selectedNode = ref<TreeNode>();

/**
 * Defines the tree to select disposable group only.
 */
const selectDisposableOnly = ref(false);

/**
 * Use as v-model selection keys to store temporary selected tree keys.
 * If the dialog closed by clicking Select button, the temporary value will be assigned to 'selectedTreeKeys'.
 * Otherwise temprorary data will be reseted.
 *
 * Also stores previously selected keys as number[].
 */
const temporarySelectedTreeKeys = ref<TreeSelectionKeys | number[]>();
const temporarySelectedNode = ref<TreeNode>();

const dialogHeader = computed(() => {
  if (props.header) return props.header;
  return props.type === 'group' ? 'Select Group' : 'Select Category';
});

const onButtonSelectClicked = (close = true): void => {
  selectedTreeKeys.value = temporarySelectedTreeKeys.value;
  selectedNode.value = temporarySelectedNode.value;

  if (Object.keys(selectedTreeKeys.value ?? {}).length) {
    emit('select', {
      keys: selectedTreeKeys.value,
      selectedNodes: selectedTreeNodes.value,
      singleNode: selectedNode.value,
    });

    visible.value = !close;
  } else if (props.allowEmptySelection) {
    emit('select', {
      keys: [],
      selectedNodes: undefined,
      singleNode: undefined,
    });

    visible.value = !close;
  } else if (!props.allowEmptySelection) {
    invalidSelectionMessage.value =
      'You must pick a ' + props.type.toLowerCase();
  }
};

watch(visible, (show) => {
  temporarySelectedTreeKeys.value = show ? selectedTreeKeys.value : undefined;

  temporarySelectedNode.value = show ? selectedNode.value : undefined;

  if (show && props.keys && !temporarySelectedTreeKeys.value) {
    temporarySelectedTreeKeys.value = props.keys;
  }
});

watch(
  treeComponent,
  (tree) => {
    if (tree && props.showDisposableGroups) {
      tree.showDisposableGroups = true;
      selectDisposableOnly.value = true;
    }
  },
  { deep: true },
);

watch(
  selectedTreeNodes,
  () => {
    if (props.hidden) {
      onButtonSelectClicked(false);
    }
  },
  { once: true },
);

watch(
  () => props.keys,
  () => {
    selectedTreeKeys.value = props.keys;
  },
);

defineExpose({ treeComponent });
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :modal="!hidden"
    :pt="{
      root: {
        class: [
          ...DialogPreset.root({ state: {} }).class,
          '!w-[400px]',
          { '!hidden': hidden },
        ],
      },
      header: {
        class: 'relative',
      },
      icons: {
        class: 'absolute top-0 right-0',
      },
      content: {
        class: [
          ...DialogPreset.content({ state: {}, instance: {} }).class,
          'pr-1.5 focus-visible:outline-none',
        ],
      },
      footer: {
        class: [
          {
            'flex justify-end items-center gap-1': allowEmptySelection,
            'flex justify-end items-end flex-col gap-1': !allowEmptySelection,
          },
        ],
      },
    }"
    @hide="$emit('hide')"
    @show="invalidSelectionMessage = ''"
    data-wv-name="dialogselecttree"
  >
    <template #header>
      <slot
        :dialog-header="dialogHeader"
        :filter="treeFilter"
        :is-loading="!!treeComponent?.isLoading"
        :list-label="listLabel"
        :lists="lists"
        :sub-header="subHeader"
        :update-filter="(filter: string) => (treeFilter = filter)"
        name="header"
      >
        <div
          class="flex flex-col gap-3 items-start w-full"
          data-wv-section="header-wrapper"
        >
          <h3 :class="[...DialogPreset.title.class, '!leading-6']">
            {{ dialogHeader }}
          </h3>

          <ul
            v-if="lists"
            class="list-inside w-full overflow-auto max-h-20"
            data-wv-section="dialog-select-tree-lists"
          >
            <li
              :key="index"
              v-for="(list, index) in lists"
              class="text-xs leading-[16.39px] tracking-[0.02em] font-medium text-general-800 dark:text-white list-disc ps-[6px]"
            >
              {{
                listLabel && typeof list !== 'string'
                  ? getNestedPropertyValue(list, listLabel)
                  : list
              }}
            </li>
          </ul>

          <h4
            v-if="subHeader"
            :class="[...DialogPreset.title.class, '!leading-6 text-sm']"
          >
            {{ subHeader }}
          </h4>

          <TreeSearchInput
            v-if="!treeComponent?.isLoading"
            v-model="treeFilter"
            class="w-full"
          />
        </div>
      </slot>
    </template>

    <Tree
      ref="treeComponent"
      v-bind="props"
      v-model:selected-keys="temporarySelectedTreeKeys"
      v-model:selected-tree-nodes="selectedTreeNodes"
      :auto-select-all="autoSelectAll"
      :disable-keys="disableKeys"
      :disable-node-all="disableNodeAll"
      :disable-node-when="disableNodeWhen"
      :exact-disable-key="exactDisableKey"
      :exact-disable-keys="exactDisableKeys"
      :excluded-keys="excludedKeys"
      :fetch-tree="fetchTree"
      :filter="treeFilter"
      :flatten-disposable-node="flattenDisposableNode"
      :params="params"
      :propagate-selection="propagateSelection"
      :readonly="props.readonly"
      :select-disposable-only="selectDisposableOnly"
      :selection-mode="selectionMode"
      :type="props.type"
      @node-select="temporarySelectedNode = $event"
      @update:selected-keys="invalidSelectionMessage = ''"
    >
      <template #default="{ node, checked, disabled }" v-if="$slots.treenode">
        <slot
          :checked="checked"
          :disabled="disabled"
          :node="node"
          name="treenode"
        />
      </template>
    </Tree>

    <template #footer v-if="!props.readonly">
      <slot name="footer">
        <ValidatorMessage
          v-if="!allowEmptySelection"
          v-show="!!invalidSelectionMessage"
          :format="false"
          :message="invalidSelectionMessage"
        />

        <div
          class="flex gap-1 items-center justify-end"
          data-wv-section="footer-button"
        >
          <Button
            @click="visible = false"
            label="Cancel"
            severity="secondary"
            text
          />

          <Button
            :disabled="treeComponent?.isLoading"
            @click="onButtonSelectClicked(true)"
            label="Select"
            severity="success"
          />
        </div>
      </slot>
    </template>
  </Dialog>
</template>
